import {
  type HTMLAttributes,
  type ReactNode,
  Suspense,
  lazy,
  useMemo,
} from 'react';

import type { FileWithPath } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { useTheme } from '@pulse-web-ui/theme';

import { convertMbToBytes } from '../../../../utils';
import { ColumnContainer } from '../../../containers';
import { useFiles } from '../hooks';
import {
  ButtonAdd,
  ErrorText,
  InputContainer,
  Label,
  List,
  Messages,
  RotatedIcon,
} from './file-input.styles';
import { FileItem } from './file-item';

const Plus = lazy(() => import('@pulse-web-ui/icons/lib/lazy/close'));

interface FileTypes {
  [key: string]: Array<string>;
}

export type ErrorMessages = {
  [key: string]: (fileName: string) => string;
};

export interface FileInputProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  onFilesUpdate: (files: FileWithPath[]) => void;
  hint?: ReactNode;
  acceptedTypes?: FileTypes;
  maxFiles?: number;
  maxFileSize?: number;
  formError?: string;
  errorMessages?: ErrorMessages;
  testId?: string;
}

export const FileInput = ({
  className,
  label,
  hint,
  acceptedTypes,
  maxFiles,
  maxFileSize,
  formError,
  errorMessages,
  onFilesUpdate,
  testId = 'file-input',
}: FileInputProps) => {
  const theme = useTheme();

  const { files, addFiles, removeFile, generateInputErrors, inputErrors } =
    useFiles(onFilesUpdate, errorMessages);

  const isMaxFilesUploaded = files.length === maxFiles;
  const maxSize = maxFileSize ? convertMbToBytes(maxFileSize) : undefined;

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedTypes,
    onDrop: addFiles,
    onDropRejected: generateInputErrors,
    maxFiles,
    maxSize,
    disabled: isMaxFilesUploaded,
  });

  const PlusIcon = useMemo(
    () => (
      <Suspense>
        <RotatedIcon>
          <Plus
            color={theme.colors.icon.primary}
            viewBox='0 0 24 24'
            width={24}
          />
        </RotatedIcon>
      </Suspense>
    ),
    []
  );

  return (
    <div className={className}>
      <InputContainer
        {...getRootProps()}
        data-testid={testId}
      >
        <input {...getInputProps()} />
        {!files.length && (
          <Label>
            {PlusIcon}
            {label}
          </Label>
        )}
        {!!files.length && !isMaxFilesUploaded && (
          <ButtonAdd>{PlusIcon}</ButtonAdd>
        )}
        {
          <List>
            {files.map((file) => {
              return (
                <FileItem
                  key={`${file.name}-${file.lastModified}`}
                  file={file}
                  onRemove={removeFile(file)}
                  testId={testId}
                />
              );
            })}
          </List>
        }
      </InputContainer>
      <Messages>
        <ColumnContainer $gap={4}>
          {formError && <ErrorText>{formError}</ErrorText>}
          {!!inputErrors?.length &&
            inputErrors.map((error) => <ErrorText>{error}</ErrorText>)}
        </ColumnContainer>
        {hint}
      </Messages>
    </div>
  );
};
